<template>
  <div class="stage" :class="{ 'is-fading': fading }">
    <div class="split">
      <!-- Left: orbit + cursor-tracked starfield -->
      <div ref="orbitSide" class="orbit-side">
        <canvas ref="canvasEl" class="stars" aria-hidden="true" />
        <div class="orbit-stage" aria-hidden="true">
          <div class="orbit-inner">
            <div class="ring r4" />
            <div class="ring r3" />
            <div class="ring r2" />
            <div class="ring r1" />
            <div class="satellite s1" />
            <div class="satellite s2" />
            <div class="satellite s3" />
            <div class="satellite s4" />
            <div class="core">
              <div class="core-ball" />
              <div class="logo-f">
                <!-- Foodics F glyph extracted from the wordmark SVG -->
                <svg viewBox="0 0 130 130" xmlns="http://www.w3.org/2000/svg">
                  <g transform="translate(-58 -52)">
                    <path
                      fill="#FFFFFF"
                      d="M101.4,97.3c0-4.5,3.6-8.1,8.1-8.1h77.9V60.6h-77.2c-20.7,0-37.4,16.8-37.4,37.4v4.8v7.7
                        c0,20.7,16.7,37.4,37.4,37.4h40.7v-28.6h-41.4c-4.5,0-8.1-3.6-8.1-8.1v-8.3L101.4,97.3L101.4,97.3z"
                    />
                    <path
                      fill="#FFFFFF"
                      d="M98.9,161.9L98.9,161.9c-1-1-2.3-1.8-3.7-2.4c-1.4-0.6-3-0.9-4.6-0.9h-6.4c-1.7,0-3.2,0.3-4.7,0.9
                        c-1.4,0.6-2.7,1.4-3.7,2.4c-1,1-1.9,2.3-2.4,3.7s-0.9,3-0.9,4.7v5c0,1.7,0.3,3.3,0.9,4.7s1.4,2.6,2.4,3.7c1,1,2.3,1.9,3.7,2.4
                        c1.4,0.6,3,0.9,4.6,0.9h6.4c1.7,0,3.2-0.3,4.6-0.9c1.4-0.6,2.7-1.4,3.7-2.4c1-1.1,1.8-2.3,2.4-3.7c0.6-1.4,0.9-3,0.9-4.7v-5
                        c0-1.7-0.3-3.2-0.9-4.6C100.8,164.2,99.9,162.9,98.9,161.9"
                    />
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="tagline">
          <div class="eye"><span class="dot" /> Foodics · Sprint Scorecard</div>
          <h2>Every sprint <span class="serif">in orbit.</span></h2>
          <p>Triage hygiene, predict carryover, and read AI synthesis — all in one view.</p>
        </div>
      </div>

      <!-- Right: sign-in form (logo-less by design, SSO-only) -->
      <div class="form-side">
        <div class="form-card" data-test="signin-form">
          <h1>Welcome <span class="serif">Unicorn builder</span></h1>
          <p class="sub">Sign in to your scorecard.</p>

          <button
            class="sso"
            type="button"
            data-test="signin-google"
            @click="signIn('Google')"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.6 5.1C9.6 39.5 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <button
            class="sso sso-zoho"
            type="button"
            data-test="signin-zoho"
            @click="signIn('Zoho')"
          >
            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#E42527" d="M8 14h32l-22 24h22v6H8l22-24H8z"/>
            </svg>
            Continue with Zoho
          </button>
        </div>

        <div class="brand-line">
          Built with <span class="heart">♥</span> by the AI Foodics team
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from "vue";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();

const orbitSide = ref<HTMLDivElement | null>(null);
const canvasEl = ref<HTMLCanvasElement | null>(null);
const fading = ref(false);

interface Star {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  twinkle: number;
  hue: "brand" | "glow" | "saffron";
}

const COLORS: Record<Star["hue"], { fill: string; glow: string }> = {
  brand:   { fill: "rgba(155,107,255,",  glow: "rgba(155,107,255,.6)"  },
  glow:    { fill: "rgba(200,166,255,",  glow: "rgba(200,166,255,.55)" },
  saffron: { fill: "rgba(232,163,61,",   glow: "rgba(232,163,61,.6)"   },
};

let stars: Star[] = [];
let raf = 0;
let mouseX = -9999;
let mouseY = -9999;
let dpr = 1;
let canvasWidth = 0;
let canvasHeight = 0;
let resizeObserver: ResizeObserver | null = null;

function initStars(): void {
  const count = Math.max(80, Math.min(160, Math.round((canvasWidth * canvasHeight) / (18000 * dpr * dpr))));
  stars = Array.from({ length: count }, () => ({
    x: Math.random() * canvasWidth,
    y: Math.random() * canvasHeight,
    vx: (Math.random() - 0.5) * 0.18 * dpr,
    vy: (Math.random() - 0.5) * 0.18 * dpr,
    r: (Math.random() * 1.4 + 0.4) * dpr,
    twinkle: Math.random() * Math.PI * 2,
    hue: Math.random() < 0.18 ? "saffron" : Math.random() < 0.5 ? "brand" : "glow",
  }));
}

function resize(): void {
  const canvas = canvasEl.value;
  const host = orbitSide.value;
  if (!canvas || !host) return;
  const r = host.getBoundingClientRect();
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvasWidth = r.width * dpr;
  canvasHeight = r.height * dpr;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  canvas.style.width = `${r.width}px`;
  canvas.style.height = `${r.height}px`;
}

function tick(): void {
  const canvas = canvasEl.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  const LINK = 110 * dpr;
  const CURSOR = 220 * dpr;

  // star ↔ star lines
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const a = stars[i];
      const b = stars[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < LINK * LINK) {
        const op = (1 - Math.sqrt(d2) / LINK) * 0.18;
        ctx.strokeStyle = `rgba(155,107,255,${op})`;
        ctx.lineWidth = dpr * 0.5;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }
  }

  // cursor → star lines
  if (mouseX > -9000) {
    for (const s of stars) {
      const dx = s.x - mouseX;
      const dy = s.y - mouseY;
      const d2 = dx * dx + dy * dy;
      if (d2 < CURSOR * CURSOR) {
        const d = Math.sqrt(d2);
        const op = (1 - d / CURSOR) * 0.55;
        ctx.strokeStyle = `rgba(200,166,255,${op})`;
        ctx.lineWidth = dpr * 0.8;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(mouseX, mouseY);
        ctx.stroke();
      }
    }
    const grad = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 40 * dpr);
    grad.addColorStop(0, "rgba(200,166,255,.5)");
    grad.addColorStop(1, "rgba(200,166,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(mouseX, mouseY, 40 * dpr, 0, Math.PI * 2);
    ctx.fill();
  }

  // stars
  for (const s of stars) {
    s.x += s.vx;
    s.y += s.vy;
    s.twinkle += 0.022;
    if (s.x < 0 || s.x > canvasWidth) s.vx *= -1;
    if (s.y < 0 || s.y > canvasHeight) s.vy *= -1;
    const tw = 0.6 + Math.sin(s.twinkle) * 0.4;
    const c = COLORS[s.hue];
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fillStyle = `${c.fill}${tw})`;
    ctx.shadowColor = c.glow;
    ctx.shadowBlur = s.r * 4;
    ctx.fill();
  }
  ctx.shadowBlur = 0;
  raf = requestAnimationFrame(tick);
}

function onMove(e: MouseEvent): void {
  const host = orbitSide.value;
  if (!host) return;
  const r = host.getBoundingClientRect();
  mouseX = (e.clientX - r.left) * dpr;
  mouseY = (e.clientY - r.top) * dpr;
}
function onLeave(): void {
  mouseX = -9999;
  mouseY = -9999;
}

type IdP = "Google" | "Zoho";

function signIn(idp: IdP): void {
  if (fading.value) return;
  fading.value = true;
  // The fade out gives the visual moment promised by the design;
  // Cognito then takes over for the actual auth.
  window.setTimeout(() => {
    auth.startSignIn(idp);
  }, 480);
}

onMounted(() => {
  resize();
  initStars();

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const host = orbitSide.value;
  if (host) {
    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
    resizeObserver = new ResizeObserver(() => {
      resize();
      initStars();
    });
    resizeObserver.observe(host);
  }
  if (!reduced) raf = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
  if (raf) cancelAnimationFrame(raf);
  resizeObserver?.disconnect();
  resizeObserver = null;
  const host = orbitSide.value;
  if (host) {
    host.removeEventListener("mousemove", onMove);
    host.removeEventListener("mouseleave", onLeave);
  }
});
</script>

<style scoped>
.stage {
  position: fixed; inset: 0;
  overflow: hidden;
  background: var(--bg);
  color: var(--ink);
  transition: opacity 480ms ease;
}
.stage.is-fading { opacity: 0; }

.split {
  position: absolute; inset: 0;
  display: grid; grid-template-columns: 1.1fr 1fr;
}

/* === LEFT: orbit + interactive stars === */
.orbit-side {
  position: relative; overflow: hidden;
  background: radial-gradient(ellipse 110% 80% at 30% 50%, #1A0848 0%, #0E0428 60%, #06011A 100%);
}
.stars {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  z-index: 1;
}
.orbit-stage {
  position: absolute; inset: 0;
  display: grid; place-items: center;
  z-index: 2; pointer-events: none;
}
.orbit-inner { position: relative; width: 1px; height: 1px; }

.core {
  position: relative;
  width: 140px; height: 140px;
  left: -70px; top: -70px;
}
.core-ball {
  position: absolute; inset: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 28%, #C8A6FF 0%, var(--brand-2) 40%, var(--brand) 70%, var(--brand-deep) 100%);
  box-shadow:
    0 0 80px rgba(155, 107, 255, .55),
    0 0 160px rgba(68, 0, 153, .5),
    inset 0 -20px 40px rgba(0, 0, 0, .3),
    inset 0 12px 24px rgba(255, 255, 255, .15);
  animation: orbitPulse 3.4s ease-in-out infinite;
}
@keyframes orbitPulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
}
.logo-f {
  position: absolute; inset: 0;
  display: grid; place-items: center;
}
.logo-f svg {
  width: 62px; height: auto;
  filter: drop-shadow(0 2px 8px rgba(255, 255, 255, .4));
}

.ring {
  position: absolute; left: 0; top: 0;
  border: 1px solid rgba(155, 107, 255, .22);
  border-radius: 50%;
  transform-origin: center;
}
.r1 { width: 280px; height: 280px; left: -140px; top: -140px; animation: rot 18s linear infinite; }
.r2 { width: 440px; height: 440px; left: -220px; top: -220px; animation: rot 28s linear infinite reverse; border-style: dashed; border-color: rgba(232, 163, 61, .22); }
.r3 { width: 620px; height: 620px; left: -310px; top: -310px; animation: rot 40s linear infinite; border-color: rgba(155, 107, 255, .14); }
.r4 { width: 820px; height: 820px; left: -410px; top: -410px; animation: rot 60s linear infinite reverse; border-color: rgba(255, 255, 255, .05); }
@keyframes rot { to { transform: rotate(360deg); } }

.satellite {
  position: absolute; left: 0; top: 0;
  width: 14px; height: 14px;
  border-radius: 50%;
  margin-left: -7px; margin-top: -7px;
}
.s1 { background: #9B6BFF; box-shadow: 0 0 24px #9B6BFF, 0 0 48px rgba(155, 107, 255, .5); animation: orbit1 18s linear infinite; }
.s2 { background: var(--gold); box-shadow: 0 0 20px var(--gold); animation: orbit2 28s linear infinite; width: 10px; height: 10px; margin-left: -5px; margin-top: -5px; }
.s3 { background: #fff; box-shadow: 0 0 16px #fff; animation: orbit3 40s linear infinite; width: 8px; height: 8px; margin-left: -4px; margin-top: -4px; }
.s4 { background: #00B98E; box-shadow: 0 0 18px #00B98E; animation: orbit2 22s linear infinite reverse; width: 9px; height: 9px; margin-left: -4.5px; margin-top: -4.5px; }
@keyframes orbit1 { from { transform: rotate(0) translateX(140px) rotate(0); } to { transform: rotate(360deg) translateX(140px) rotate(-360deg); } }
@keyframes orbit2 { from { transform: rotate(0) translateX(220px) rotate(0); } to { transform: rotate(360deg) translateX(220px) rotate(-360deg); } }
@keyframes orbit3 { from { transform: rotate(0) translateX(310px) rotate(0); } to { transform: rotate(360deg) translateX(310px) rotate(-360deg); } }

/* tagline */
.tagline {
  position: absolute;
  bottom: 54px; left: 54px; right: 54px;
  color: rgba(250, 246, 240, .92);
  z-index: 5; pointer-events: none;
}
.tagline .eye {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.2em;
  color: var(--brand-glow); text-transform: uppercase;
  margin-bottom: 14px;
  display: flex; align-items: center; gap: 8px;
}
.tagline .eye .dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--brand-glow);
  box-shadow: 0 0 8px var(--brand-glow);
  animation: blink 1.6s ease-in-out infinite;
}
@keyframes blink { 50% { opacity: .3; } }
.tagline h2 {
  font-size: 34px; font-weight: 500;
  letter-spacing: -0.025em; line-height: 1.15;
  margin: 0 0 10px; max-width: 440px;
}
.tagline h2 .serif {
  font-family: "Instrument Serif", serif;
  font-style: italic; color: var(--brand-glow);
}
.tagline p {
  font-size: 13px; color: rgba(250, 246, 240, .55);
  max-width: 380px; line-height: 1.55; margin: 0;
}

/* === RIGHT: form (logo-less by design) === */
.form-side {
  display: grid; place-items: center;
  padding: 60px 40px;
  position: relative;
  background: var(--bg);
}
.form-card {
  width: min(380px, 100%);
  opacity: 0;
  animation: fadeUp 1.1s var(--ease-reveal) .4s forwards;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}
h1 {
  font-family: "Inter Tight", sans-serif;
  font-size: 48px; font-weight: 500;
  letter-spacing: -0.04em; line-height: 1;
  margin: 0 0 10px;
  color: var(--ink);
}
h1 .serif {
  font-family: "Instrument Serif", serif;
  font-style: italic; color: var(--brand);
}
.sub {
  font-size: 14px; color: #6B7A82;
  margin: 0 0 36px; line-height: 1.55;
}
.field { margin-bottom: 14px; }
.field label {
  display: block;
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.14em;
  color: #6B7A82; text-transform: uppercase;
  margin-bottom: 6px;
}
.field input {
  width: 100%; padding: 13px 16px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 14px; font-family: inherit;
  color: var(--ink);
  transition: border-color var(--speed-standard) var(--ease),
              box-shadow var(--speed-standard) var(--ease);
}
.field input::placeholder { color: #A0ABB1; }
.field input:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 15%, transparent);
}

.btn {
  width: 100%; padding: 14px;
  border: 0; cursor: pointer;
  background: var(--brand); color: #fff;
  font-family: inherit; font-weight: 500; font-size: 14px;
  border-radius: 10px;
  margin-top: 8px;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  letter-spacing: 0.01em;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--brand) 30%, transparent);
  transition: background var(--speed-standard) var(--ease),
              transform var(--speed-standard) var(--ease),
              box-shadow var(--speed-standard) var(--ease);
}
.btn:hover {
  background: var(--brand-2);
  transform: translateY(-1px);
  box-shadow: 0 12px 32px color-mix(in srgb, var(--brand) 45%, transparent);
}
.btn:active { transform: translateY(1px); }
.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 22%, transparent),
              0 8px 24px color-mix(in srgb, var(--brand) 30%, transparent);
}
.btn .arrow { transition: transform var(--speed-standard) var(--ease); }
.btn:hover .arrow { transform: translateX(4px); }

.foot {
  display: flex; justify-content: space-between;
  font-size: 12px; color: #6B7A82;
  margin-top: 24px;
}
.foot a {
  color: var(--brand);
  cursor: pointer;
  font-weight: 500;
}
.or {
  display: flex; align-items: center; gap: 12px;
  margin: 24px 0;
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; color: #A0ABB1;
  letter-spacing: 0.14em; text-transform: uppercase;
}
.or::before, .or::after {
  content: ""; flex: 1; height: 1px;
  background: var(--line);
}
.sso {
  width: 100%; padding: 14px;
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px; font-family: inherit; font-weight: 500;
  color: var(--ink);
  display: flex; align-items: center; justify-content: center; gap: 10px;
  letter-spacing: 0.01em;
  transition: border-color var(--speed-standard) var(--ease),
              transform var(--speed-standard) var(--ease),
              box-shadow var(--speed-standard) var(--ease);
}
.sso + .sso { margin-top: 10px; }
.sso:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--brand) 12%, transparent);
}
.sso:active { transform: translateY(1px); }
.sso:focus-visible {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}
.sso-zoho:hover {
  border-color: #E42527;
  box-shadow: 0 8px 24px color-mix(in srgb, #E42527 14%, transparent);
}
.sso-zoho:focus-visible {
  border-color: #E42527;
  box-shadow: 0 0 0 3px color-mix(in srgb, #E42527 20%, transparent);
}

.brand-line {
  position: absolute;
  bottom: 18px; right: 30px;
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.2em;
  color: #6B7A82; text-transform: uppercase;
}
.brand-line .heart {
  color: var(--brand);
  animation: heartbeat 1.6s ease-in-out infinite;
  display: inline-block;
}
@keyframes heartbeat {
  0%, 60%, 100% { transform: scale(1); }
  20% { transform: scale(1.25); }
  40% { transform: scale(1.1); }
}

@media (max-width: 780px) {
  .split { grid-template-columns: 1fr; }
  .orbit-side { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .form-card, .core-ball, .ring, .satellite, .tagline .eye .dot, .brand-line .heart {
    animation: none !important;
  }
  .form-card { opacity: 1; }
}
</style>
